/**
 * Pure unit conversions for the Text behavior.
 *
 * Spec units (em, em-fraction) ↔ MSDF renderer units (font units, sigDist).
 * Kept separate from the behavior class so they can be unit-tested without
 * spinning up a Babylon engine. See scenedata/behaviors/textdata.ts for the
 * spec.
 */

export interface FontMetrics {
    /** Em-square size in font units (BMFont `info.size`). */
    emSize: number;
    /** MSDF distance range in pixels (BMFont `distanceField.distanceRange`). */
    distanceRange: number;
    /** Natural line-height as a fraction of em-square (BMFont `common.lineHeight / info.size`). */
    naturalLineHeightEm: number;
    /** Depth of the cap line below the line top, font units (`capTopFontUnits` in bmfont.ts). */
    capTop: number;
}

/**
 * Convert spec `fontSize` to the world-space scale applied to the renderer's
 * em-square output. `fontSize: 100` means a 1 world-unit em-square, and the
 * renderer draws glyphs at em-square = 1.0 in its local space, so the scale
 * is just `fontSize / 100`.
 */
export function fontSizeToWorldScale(fontSize: number): number {
    return fontSize / 100;
}

/**
 * Convert spec `letterSpacing` (em units) to font-unit advance offset
 * suitable for the layout engine.
 */
export function letterSpacingToFontUnits(letterSpacingEm: number, metrics: FontMetrics): number {
    return letterSpacingEm * metrics.emSize;
}

/**
 * Convert spec `lineHeight` (multiplier of em-square) to the renderer's
 * `lineHeight` (multiplier of the font's natural `common.lineHeight`).
 *
 * Spec semantics match CSS `line-height`: `1.0` is one em of vertical
 * advance, `1.2` is the typical body default. Babylon's MSDF renderer
 * applies its `lineHeight` option as a multiplier of `common.lineHeight`,
 * so we divide out the natural-line-height factor to land at em units.
 *
 * `commonLineHeightFontUnits` is the font's `common.lineHeight` value
 * straight from the BMFont JSON.
 */
export function lineHeightEmToRendererMultiplier(
    lineHeightEm: number,
    metrics: FontMetrics,
    commonLineHeightFontUnits: number,
): number {
    if (commonLineHeightFontUnits <= 0) return lineHeightEm;
    return (lineHeightEm * metrics.emSize) / commonLineHeightFontUnits;
}

/**
 * Convert spec `strokeWidth` (em fraction) to the MSDF renderer's
 * `strokeOutsetWidth` (signed-distance units).
 *
 * The MSDF shader's `sigDist` is the signed distance to the glyph edge,
 * normalised by `distanceRange` (so `sigDist = 0.5` corresponds to the full
 * half-distance-range). A stroke `f` em wide is `f × emSize` atlas pixels;
 * the shader's `uStrokeOutsetWidth × 0.5` is added to `sigDist`, so the
 * conversion is `(f × emSize) / (distanceRange / 2) = 2 × f × emSize /
 * distanceRange`. Saturates at `uStrokeOutsetWidth = 2` because the SDF
 * itself only encodes distances within `±distanceRange/2`.
 */
export function strokeWidthToSigDist(emFraction: number, metrics: FontMetrics): number {
    if (metrics.distanceRange <= 0) return 0;
    return (2 * emFraction * metrics.emSize) / metrics.distanceRange;
}

/**
 * Map an anchor enum value to its 0..1 fraction along the X axis.
 * left=0, center=0.5, right=1.
 */
export function anchorXFraction(anchor: 'left' | 'center' | 'right'): number {
    switch (anchor) {
        case 'left': return 0;
        case 'center': return 0.5;
        case 'right': return 1;
    }
}

/**
 * Compute the world-space Y translation needed so that the SceneObject origin
 * sits at the requested vertical anchor of the text box.
 *
 * Inputs are em-relative pre-scale Y bounds (top is positive-up,
 * `yTopMaxEm > yBotMinEm`). Output is in world units (post-scale).
 */
export function computeAnchorYOffset(
    anchorY: 'top' | 'middle' | 'bottom',
    yTopMaxEm: number,
    yBotMinEm: number,
    worldScale: number,
): number {
    switch (anchorY) {
        case 'top':    return -yTopMaxEm * worldScale;
        case 'middle': return -((yTopMaxEm + yBotMinEm) / 2) * worldScale;
        case 'bottom': return -yBotMinEm * worldScale;
    }
}
